import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, X, FileText, AlertTriangle, CheckCircle2, Loader2,
  Calendar, MapPin, Clock, Link2, ChevronRight, HelpCircle, Copy
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import ModalPortal from './ModalPortal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EventPDFImportDialog
 *   Two-step admin tool:
 *     1. Upload PDF → backend parses & returns structured events (preview)
 *     2. Admin edits/removes rows → clicks "Import"  → backend bulk-inserts,
 *        dedup by (title + date).
 *
 *   Props:
 *     open, onClose(), onImported(result) — fires after successful commit.
 */
export const EventPDFImportDialog = ({ open, onClose, onImported }) => {
  const [stage, setStage] = useState('upload'); // 'upload' | 'preview' | 'done'
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null); // { total, valid, invalid, duplicates, events }
  const [events, setEvents] = useState([]);       // editable copy
  const [busy, setBusy] = useState(false);
  const [showFormat, setShowFormat] = useState(false);

  // Lock body scroll while open
  useBodyScrollLock(!!open);
  const inputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setStage('upload');
    setFile(null);
    setPreview(null);
    setEvents([]);
    setBusy(false);
    setShowFormat(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are supported');
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error('PDF exceeds 15 MB limit');
      return;
    }
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/admin/events/import-pdf/preview`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      // Seed editable rows (skip duplicates by default, keep all visible so admin can decide)
      setEvents(res.data.events.map(e => ({
        ...e,
        _selected: !!e.is_valid && !e.is_duplicate,
      })));
      setStage('preview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse PDF');
    } finally {
      setBusy(false);
    }
  };

  const updateRow = (idx, patch) => {
    setEvents(prev => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const handleCommit = async () => {
    const selected = events.filter(e => e._selected);
    const missingRequired = selected.filter(e => !e.title || !e.date);
    if (missingRequired.length > 0) {
      toast.error(`${missingRequired.length} selected row${missingRequired.length !== 1 ? 's' : ''} missing title or date`);
      return;
    }
    if (selected.length === 0) {
      toast.error('Select at least one valid event');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        events: selected.map(e => ({
          title: e.title,
          event_name: e.event_name || null,
          date: e.date,
          time: e.time || null,
          location: e.location || null,
          category: e.category || null,
          description: e.description || null,
          recap: e.recap || null,
          notes: e.notes || null,
          external_link: e.external_link || null,
        })),
      };
      const res = await axios.post(`${API}/admin/events/import-pdf/commit`, payload, {
        withCredentials: true,
      });
      toast.success(`Imported ${res.data.created_count} event${res.data.created_count !== 1 ? 's' : ''}${res.data.skipped_count > 0 ? ` · ${res.data.skipped_count} skipped` : ''}`);
      onImported?.(res.data);
      setStage('done');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to import events');
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = events.filter(e => e._selected).length;

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="event-pdf-import-dialog">
        <div className="bg-white w-full max-w-5xl max-h-[92vh] rounded-sm shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#002249]" />
            <h2 className="text-base font-bold text-[#002249]">Import Events from PDF</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFormat(s => !s)}
              className="text-[12px] text-slate-500 hover:text-[#002249] inline-flex items-center gap-1"
              data-testid="show-format-btn"
            >
              <HelpCircle className="w-3.5 h-3.5" /> Format Guide
            </button>
            <button
              onClick={handleClose}
              className="p-1 text-slate-400 hover:text-slate-700"
              data-testid="close-import-dialog-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Format guide collapsible */}
        {showFormat && (
          <div className="px-5 py-3 bg-blue-50 border-b border-blue-200 text-[12px] text-blue-900 font-mono" data-testid="format-guide">
            <div className="flex justify-between items-start mb-2">
              <span className="font-sans font-semibold">Canonical PDF format — each event labeled, separated by "---"</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(FORMAT_TEMPLATE);
                  toast.success('Template copied');
                }}
                className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 font-sans text-[11px]"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <pre className="whitespace-pre-wrap text-[11px] leading-relaxed max-h-56 overflow-auto bg-white/60 p-2 rounded">{FORMAT_TEMPLATE}</pre>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {stage === 'upload' && (
            <div className="p-6 sm:p-10">
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={e => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="pdf-import-input"
              />
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
                onClick={() => inputRef.current?.click()}
                className={`cursor-pointer rounded-sm border-2 border-dashed p-10 text-center transition-colors ${dragOver ? 'border-[#002249] bg-[#002249]/5' : 'border-slate-300 hover:border-[#002249]/40'}`}
                data-testid="pdf-dropzone"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-10 h-10 text-[#002249] mx-auto mb-2 animate-spin" />
                    <p className="text-sm font-semibold text-[#002249]">Parsing PDF…</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-[#002249]">Drag & drop a PDF, or click to select</p>
                    <p className="text-[12px] text-slate-500 mt-1">Follows the DCCF Event Import format · up to 15 MB</p>
                  </>
                )}
              </div>
              <p className="mt-4 text-[12px] text-slate-500">
                The PDF should contain one or more structured event blocks, separated by <code className="font-mono bg-slate-100 px-1">---</code>.
                Each block uses labels like <strong>Title</strong>, <strong>Date</strong>, <strong>Location</strong>, etc.
                Click <strong>Format Guide</strong> above to copy the template.
              </p>
            </div>
          )}

          {stage === 'preview' && preview && (
            <div className="p-4 sm:p-5">
              {/* Summary bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                <StatTile label="Total parsed" value={preview.total} icon={FileText} color="slate" />
                <StatTile label="Valid" value={preview.valid} icon={CheckCircle2} color="emerald" />
                <StatTile label="Invalid" value={preview.invalid} icon={AlertTriangle} color="amber" />
                <StatTile label="Duplicates" value={preview.duplicates} icon={AlertTriangle} color="rose" />
              </div>

              {events.length === 0 ? (
                <div className="p-10 text-center text-slate-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No events found in the PDF.</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="preview-events-list">
                  {events.map((e, i) => (
                    <EventRow
                      key={i}
                      event={e}
                      index={i}
                      onChange={(patch) => updateRow(i, patch)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {stage === 'done' && (
            <div className="p-10 text-center">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-[#002249]">Import Complete</h3>
              <p className="text-sm text-slate-500 mt-1">Events have been added. Upload photos and videos from the Events Management tabs.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-[12px] text-slate-500">
            {stage === 'preview' && <span data-testid="selection-summary">{selectedCount} / {events.length} selected</span>}
          </div>
          <div className="flex items-center gap-2">
            {stage === 'preview' && (
              <>
                <Button variant="outline" size="sm" onClick={() => setStage('upload')} disabled={busy} className="rounded-sm">Back</Button>
                <Button
                  size="sm"
                  onClick={handleCommit}
                  disabled={busy || selectedCount === 0}
                  className="bg-[#002249] hover:bg-[#001833] text-white rounded-sm"
                  data-testid="commit-import-btn"
                >
                  {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Importing…</> : <>Import {selectedCount} event{selectedCount !== 1 ? 's' : ''} <ChevronRight className="w-3.5 h-3.5 ml-1" /></>}
                </Button>
              </>
            )}
            {stage === 'done' && (
              <Button size="sm" onClick={handleClose} className="bg-[#002249] hover:bg-[#001833] text-white rounded-sm" data-testid="close-after-import-btn">Close</Button>
            )}
            {stage === 'upload' && (
              <Button variant="outline" size="sm" onClick={handleClose} className="rounded-sm">Cancel</Button>
            )}
          </div>
        </div>
      </div>
      </div>
    </ModalPortal>
  );
};

/* ---------- helpers ---------- */

const StatTile = ({ label, value, icon: Icon, color }) => {
  const colors = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-700',
  };
  return (
    <div className={`px-3 py-2 rounded-sm ${colors[color]}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
};

const EventRow = ({ event, index, onChange }) => {
  const hasError = !event.title || !event.date;
  const borderColor = event.is_duplicate
    ? 'border-rose-200 bg-rose-50/30'
    : hasError
      ? 'border-amber-200 bg-amber-50/30'
      : 'border-slate-200';

  return (
    <div className={`border rounded-sm p-3 ${borderColor}`} data-testid={`event-preview-row-${index}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!event._selected}
          onChange={e => onChange({ _selected: e.target.checked })}
          className="mt-1 w-4 h-4 accent-[#002249]"
          data-testid={`event-row-select-${index}`}
        />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px] gap-2">
            <Input
              placeholder="Title *"
              value={event.title || ''}
              onChange={e => onChange({ title: e.target.value })}
              className={`h-8 text-sm rounded-sm ${!event.title ? 'border-amber-400' : ''}`}
              data-testid={`row-title-${index}`}
            />
            <Input
              type="date"
              value={event.date || ''}
              onChange={e => onChange({ date: e.target.value })}
              className={`h-8 text-sm rounded-sm ${!event.date ? 'border-amber-400' : ''}`}
              data-testid={`row-date-${index}`}
            />
            <Input
              placeholder="Time (optional)"
              value={event.time || ''}
              onChange={e => onChange({ time: e.target.value })}
              className="h-8 text-sm rounded-sm"
              data-testid={`row-time-${index}`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
            <Input
              placeholder="Location"
              value={event.location || ''}
              onChange={e => onChange({ location: e.target.value })}
              className="h-8 text-sm rounded-sm"
              data-testid={`row-location-${index}`}
            />
            <Input
              placeholder="Category"
              value={event.category || ''}
              onChange={e => onChange({ category: e.target.value })}
              className="h-8 text-sm rounded-sm"
              data-testid={`row-category-${index}`}
            />
          </div>
          {(event.description || event.recap) && (
            <details className="group">
              <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-[#002249]">Details ({event.description ? 'description' : ''}{event.description && event.recap ? ', ' : ''}{event.recap ? 'recap' : ''})</summary>
              <div className="mt-1 space-y-1">
                {event.description && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Description</p>
                    <textarea
                      value={event.description}
                      onChange={e => onChange({ description: e.target.value })}
                      rows={2}
                      className="w-full text-[12px] border border-slate-200 rounded-sm p-2"
                      data-testid={`row-description-${index}`}
                    />
                  </div>
                )}
                {event.recap !== undefined && event.recap !== null && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Recap</p>
                    <textarea
                      value={event.recap || ''}
                      onChange={e => onChange({ recap: e.target.value })}
                      rows={2}
                      className="w-full text-[12px] border border-slate-200 rounded-sm p-2"
                      data-testid={`row-recap-${index}`}
                    />
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Badges */}
          <div className="flex items-center flex-wrap gap-1.5 text-[10px]">
            {event.is_past && <Badge color="slate"><Clock className="w-2.5 h-2.5" />Past</Badge>}
            {!event.is_past && event.date && <Badge color="emerald"><Calendar className="w-2.5 h-2.5" />Upcoming</Badge>}
            {event.location && <Badge color="blue"><MapPin className="w-2.5 h-2.5" />{event.location}</Badge>}
            {event.external_link && <Badge color="slate"><Link2 className="w-2.5 h-2.5" />link</Badge>}
            {event.is_duplicate && <Badge color="rose"><AlertTriangle className="w-2.5 h-2.5" />duplicate — will be skipped</Badge>}
            {event.warnings?.filter(w => !w.startsWith('Duplicate')).map((w, wi) => (
              <Badge key={wi} color="amber"><AlertTriangle className="w-2.5 h-2.5" />{w}</Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Badge = ({ children, color }) => {
  const palette = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
  };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-semibold ${palette[color]}`}>{children}</span>
  );
};

const FORMAT_TEMPLATE = `Title: DCCF Annual Gala 2026
Event Name: Annual Gala
Date: 2026-04-15
Time: 6:00 PM
Location: Miami Conference Center, Miami, FL
Type: gala
Description: Our flagship annual celebration bringing together
Dominican business leaders from across Florida.
Recap:
Notes: Black tie attire required.
External Link: https://dccf.org/rsvp/gala

---

Title: Small Business Workshop
Date: 03/22/2026
Time: 10:00 AM
Location: DCCF Headquarters, Miami, FL
Type: workshop
Description: Practical workshop on funding, bookkeeping, and growth.
Link: https://dccf.org/events/workshop

---

Title: Past Networking Breakfast
Date: November 10, 2025
Location: Biltmore Hotel, Coral Gables, FL
Description: Monthly breakfast for DCCF members.
Recap: Over 80 members attended.
`;

export default EventPDFImportDialog;
